import {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useActivePosEntityContext } from 'src/contexts/ActivePosEntityContext';
import { useAppContext } from 'src/contexts/AppContext';
import { useCatalogDataContext } from 'src/contexts/CatalogDataContext';
import { EmailPurchaseOrderContext } from 'src/contexts/EmailPurchaseOrderContext';
import { usePurchaseOrderDefaultValues } from 'src/hooks/api/usePurchaseOrderDefaultValues';
import { useEventItemLoadingDisplay } from 'src/hooks/useEventItemLoadingDisplay';
import { TicketGroupEditContext } from 'src/modals/PurchaseWizard/PurchaseWizard.types';
import {
  PurchaseDeeplinkQueryParam,
  PurchaseFromEmailPoQueryParam,
} from 'src/utils/constants/constants';
import { FormatContentId } from 'src/utils/constants/formatContentId';
import { getDeepLinkIdFromUrl } from 'src/utils/deepLinkUtils';
import { getEventPerformerVenue } from 'src/utils/eventWithDataUtils';
import {
  PurchaseOrderDetailsInput,
  splitOutTicketGroupId,
  toPurchaseOrderDetailsInput,
} from 'src/utils/purchaseUtils';
import {
  ActionOutboxEntityType,
  CatalogClient,
  PurchaseOrderDetails,
  Ticket,
} from 'src/WebApiController';

import { toTicketKey } from './PurchaseWizard.utils';
import { PurchaseWizardBody } from './PurchaseWizardBody';

export const PurchaseWizard = ({
  deepLinkValue,
  seedTicketGroupEditContext,
  forceNewPurchaseOrder,
}: {
  deepLinkValue?: number | string;
  /**
   * Seeds the initial ticket-group edit context beyond what the deep-link
   * encodes — used by "Copy tickets to new PO" to carry the source group (which
   * can't be serialized into the URL) into a freshly-launched new-PO wizard.
   */
  seedTicketGroupEditContext?: Partial<TicketGroupEditContext>;
  /**
   * Forces a brand-new purchase order, ignoring any PO id in the URL. Used when
   * rendering the wizard inline (e.g. "Copy tickets to new PO") from a page that
   * already has another PO in its deep-link.
   */
  forceNewPurchaseOrder?: boolean;
}) => {
  const { posEntity: purchase, setActivePosEntity } =
    useActivePosEntityContext<PurchaseOrderDetails>();

  const idParam = forceNewPurchaseOrder
    ? ''
    : getDeepLinkIdFromUrl(PurchaseDeeplinkQueryParam, window.location.href) ||
      deepLinkValue?.toString() ||
      '';

  const {
    id: activeId,
    ticketGroupId,
    ticketGroupEditReason,
    ticketGroupEditContext,
  } = useMemo(() => {
    return splitOutTicketGroupId(idParam);
  }, [idParam]);

  const purchaseOrderId = Number(activeId);

  const isFromAutoPo =
    getDeepLinkIdFromUrl(
      PurchaseFromEmailPoQueryParam,
      window.location.href
    ) === 'true';

  useEffect(() => {
    if (isFromAutoPo) {
      if (purchase?.id !== activeId) {
        setActivePosEntity(purchaseOrderId, null, true);
      }
    }
  }, [activeId, isFromAutoPo, purchase, purchaseOrderId, setActivePosEntity]);

  const { isLoading, loadingState } =
    useEventItemLoadingDisplay<PurchaseOrderDetails>(
      FormatContentId.LoadingOrderId,
      FormatContentId.SearchingForOrderId,
      FormatContentId.CouldNotFindOrderId,
      activeId?.toString()
    );

  if (purchaseOrderId > 0) {
    if (!purchase || purchase.entityType !== ActionOutboxEntityType.Purchase) {
      // If we don't have a purchase yet, either return loading state or nothing
      return loadingState;
    }

    // If we do have a purchase already, only return loadingState if it is not loading (ie, it's something else wrong)
    if (!isLoading && loadingState) return loadingState;
  }

  // For new POs (purchaseOrderId <= 0), pass null instead of the context's
  // posEntity. After a previous purchase was created, setActivePosEntity(0)
  // only clears the active entity *type* — the entity data itself persists
  // in the context. If we pass that stale entity here, PurchaseOrderDetailsForm
  // would initialize the form with the old purchase's data instead of fresh
  // defaults. For existing POs (purchaseOrderId > 0), purchase is current and
  // needed for loading/editing the purchase details.
  const purchaseForForm = purchaseOrderId > 0 ? purchase : null;

  return (
    <PurchaseOrderDetailsForm
      purchase={purchaseForForm}
      loadingState={loadingState}
      highlightTicketGroupIds={
        ticketGroupId != null ? [ticketGroupId] : undefined
      }
      initialTicketGroupEditContext={{
        editIntention: ticketGroupEditReason,
        ...ticketGroupEditContext,
        ...seedTicketGroupEditContext,
      }}
    />
  );
};

const PurchaseOrderDetailsForm = ({
  purchase,
  loadingState,
  ...rest
}: {
  purchase?: PurchaseOrderDetails | null;
  loadingState?: ReactNode;
  highlightTicketGroupIds?: number[];
  initialTicketGroupEditContext?: Partial<TicketGroupEditContext>;
}) => {
  const { loginContext, activeAccountUserId, activeAccountWebClientConfig } =
    useAppContext();
  const { partialPurchaseOrder } = useContext(EmailPurchaseOrderContext);
  const { data } = useCatalogDataContext();
  const [poInput, setPoInput] = useState<PurchaseOrderDetailsInput | null>(
    null
  );

  // Ticket lookup by ticket group id and ticket key [row, seat]
  const ticketLookup = useMemo<Record<number, Record<string, Ticket>>>(() => {
    if (!purchase) {
      return {};
    }

    return purchase.ticketGroups.reduce(
      (acc, tg) => {
        if (tg == null) {
          return acc;
        }
        const ticketsByKey = tg.tickets.reduce(
          (ticketAcc, ticket) => {
            const key = toTicketKey(ticket);
            ticketAcc[key] = ticket;
            return ticketAcc;
          },
          {} as Record<string, Ticket>
        );
        acc[tg.id] = ticketsByKey;
        return acc;
      },
      {} as Record<number, Record<string, Ticket>>
    );
  }, [purchase]);

  const createPurchaseInput = useCallback(async () => {
    const poInput = toPurchaseOrderDetailsInput(
      purchase,
      activeAccountUserId,
      loginContext?.user?.activeAccount?.currencyCode,
      data
    );

    if (poInput) {
      const tgMissingEvents = poInput?.ticketGroups?.filter(
        (tg) => tg.event == null
      );

      if (tgMissingEvents?.length) {
        const missingViagVirtualIds = tgMissingEvents?.map(
          (tg) => tg.viagogoVirtualId
        );
        const smallCatalog = await new CatalogClient(
          activeAccountWebClientConfig
        ).getCatalogFull(missingViagVirtualIds);

        tgMissingEvents.forEach((tg) => {
          const viagogoVirtualId = tg.viagogoVirtualId;

          const { event, performer, venue } = getEventPerformerVenue(
            viagogoVirtualId,
            smallCatalog
          );

          tg.event = event?.event ?? null;
          tg.performer = performer ?? null;
          tg.venue = venue ?? null;
        });
      }

      setPoInput(poInput);
    }
  }, [
    activeAccountUserId,
    activeAccountWebClientConfig,
    data,
    loginContext?.user?.activeAccount?.currencyCode,
    purchase,
  ]);

  const createDefaultValues = usePurchaseOrderDefaultValues(
    purchase,
    poInput,
    partialPurchaseOrder,
    activeAccountUserId,
    loginContext?.user?.activeAccount?.currencyCode
  );

  const methods = useForm<PurchaseOrderDetailsInput>({
    defaultValues: createDefaultValues(),
  });

  useEffect(() => {
    if (purchase && !poInput) {
      createPurchaseInput();
    } else {
      methods.reset(createDefaultValues());
    }
  }, [createDefaultValues, createPurchaseInput, methods, poInput, purchase]);

  if (!methods.formState.defaultValues) {
    // If we have purchase but unable to load the purchase default value yet, return loading state
    return loadingState;
  }

  return (
    <FormProvider {...methods}>
      <PurchaseWizardBody
        {...methods}
        {...rest}
        ticketLookup={ticketLookup}
        purchaseHasMergedListings={purchase?.hasMergedListings}
      />
    </FormProvider>
  );
};
